import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useBoolState } from 'nallian-shared-ui/lib/hooks';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import { hasRight } from 'nallian-shared-ui/lib/lib/routesHelper';
import { GetRights } from 'nallian-shared-ui/lib/selectors';
import {
  Modal,
  Dimmer,
  Loader,
  Grid,
  Button,
  ConfirmModal,
  Flex,
  List,
  Icon,
  Popup,
  Message,
} from 'nallian-shared-ui/lib/components';
import { COMMUNITY_TAB_ACTIONS, ACTIONS, COMMUNITY_DETAIL_TABS } from '../../constants';
import joinRequestService from '../../services/joinRequests/joinRequestService';
import denyJoinRequestService from '../../services/joinRequests/denyJoinRequestService';
import markUniqueJoinRequestService from '../../services/joinRequests/markUniqueJoinRequestService';
import HeaderDetail, { HeaderGrid, HeaderColumn } from '../../../app/components/HeaderDetail';
import ModalHeader from '../../../app/components/ModalHeader';
import Language from '../../../app/components/Language';
import Country from '../../../app/components/Country';
import { NoBreakSpan } from '../../../app/styles';
import useModalInModal from '../../../app/hooks/useModalInModal';
import JoinRequestStatus from './JoinRequestStatus';

const MANAGE_RIGHTS = ['JOINREQUESTS.MANAGE.ALL', 'JOINREQUESTS.MANAGE.COMMUNITY'];
const POTENTIAL_DUPLICATES_RIGHT = 'communities/join-requests/update/potential-duplicates';

const SOURCE_TYPES = {
  JOIN_REQUEST: 'joinrequest',
  ORGANISATION: 'organisation',
};

const ListContent = styled(List.Content)`
  display: flex;
  margin-bottom: 0.6rem;
  padding-bottom: 0.8rem;
  border-bottom: 1px solid rgba(34, 36, 38, 0.15);

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: 0px solid transparent;
  }

  && .header {
    width: 20rem;
  }
`;

const HeaderWithStatus = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const DuplicateRow = styled.div`
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(34, 36, 38, 0.15);
  display: flex;

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const DuplicateLabel = styled.span`
  font-weight: 600;
  margin-right: 0.5rem;
`;

const OrgLink = styled(Link)`
  color: #4183c4;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const JoinRequestDetail = ({
  open,
  rootUrl,
  dispatchJoinRequestGet,
  joinRequestFetch,
  dispatchDenyJoinRequestDelete,
  denyJoinRequestFetch,
  dispatchMarkUniqueJoinRequestPut,
  markUniqueJoinRequestFetch,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { actionId } = useParams();
  const rights = useSelector(GetRights);
  const isPotentialDuplicate = joinRequestFetch.value?.isPotentialDuplicate;
  const canManagePotentialDuplicates = hasRight(rights, POTENTIAL_DUPLICATES_RIGHT);
  const isDuplicateBlocked = isPotentialDuplicate && !canManagePotentialDuplicates;
  const canMarkUnique = isPotentialDuplicate && canManagePotentialDuplicates;

  const [modalActive, setModalActive, unsetModalActive] = useBoolState(false);

  const handleModalClose = useCallback(() => {
    unsetModalActive();
    navigate(rootUrl);
  }, [rootUrl, unsetModalActive, navigate]);

  const handleDenyClick = useCallback(
    () => dispatchDenyJoinRequestDelete(actionId, handleModalClose, handleModalClose, t),
    [t, actionId, handleModalClose, dispatchDenyJoinRequestDelete]
  );

  const handleMarkUniqueClick = useCallback(
    () => dispatchMarkUniqueJoinRequestPut(actionId, handleModalClose, handleModalClose, t),
    [t, actionId, handleModalClose, dispatchMarkUniqueJoinRequestPut]
  );

  const handleAcceptClick = useCallback(
    () => navigate(`${rootUrl}/${COMMUNITY_TAB_ACTIONS.ACCEPT_JOIN_REQUEST}/${actionId}`),
    [rootUrl, actionId, navigate]
  );

  useEffect(() => {
    if (open && actionId) {
      dispatchJoinRequestGet(actionId, handleModalClose, t);
    }
  }, [t, open, actionId, dispatchJoinRequestGet, handleModalClose]);

  useModalInModal(modalActive);

  if (!open) return null;

  return (
    <Modal open closeIcon onClose={handleModalClose}>
      <Dimmer active={joinRequestFetch.pending} inverted>
        <Loader />
      </Dimmer>
      <Modal.Header>
        <HeaderWithStatus>
          <span>{t('reviewJoinRequest')}</span>
          <JoinRequestStatus value={joinRequestFetch.value?.isPotentialDuplicate} />
        </HeaderWithStatus>
      </Modal.Header>
      <Modal.Content>
        <Flex fluid direction="column">
          <Flex>
            <ModalHeader content={t('organisation')} />
          </Flex>
          <HeaderGrid columns={1}>
            <HeaderColumn>
              <Grid divided="vertically" columns="equal">
                <HeaderDetail
                  title={t('organisationName')}
                  content={joinRequestFetch.value?.organisationName}
                />
              </Grid>
            </HeaderColumn>
            <HeaderColumn>
              <Grid divided="vertically" columns="equal">
                <HeaderDetail title={t('city')} content={joinRequestFetch.value?.city} />
                <HeaderDetail
                  title={t('country')}
                  content={<Country value={joinRequestFetch.value?.country} />}
                />
              </Grid>
            </HeaderColumn>
          </HeaderGrid>
          <Flex>
            <ModalHeader content={t('identificationCodes')} />
          </Flex>
          <List relaxed divided>
            {(joinRequestFetch.value?.identificationCodes || []).map(idcode => (
              <List.Item>
                <ListContent>
                  <List.Header>{idcode.typeTag}:</List.Header>
                  <List.Description>{idcode?.value}</List.Description>
                </ListContent>
              </List.Item>
            ))}
          </List>
          {joinRequestFetch.value?.identificationCodes?.length <= 0 && (
            <Flex margin="-1rem 0 2rem">
              <i>{t('noIdentificationCodes')}</i>
            </Flex>
          )}
          <Flex>
            <ModalHeader content={t('user')} />
          </Flex>
          <HeaderGrid columns={1}>
            <HeaderColumn>
              <Grid divided="vertically" columns="equal">
                <HeaderDetail title={t('fullName')} content={joinRequestFetch.value?.fullName} />
              </Grid>
            </HeaderColumn>
            <HeaderColumn>
              <Grid divided="vertically" columns="equal">
                <HeaderDetail
                  title={t('emailAddress')}
                  content={joinRequestFetch.value?.emailAddress}
                />
                <HeaderDetail
                  title={t('phoneNumber')}
                  content={joinRequestFetch.value?.phoneNumber}
                />
              </Grid>
            </HeaderColumn>
            <HeaderColumn>
              <Grid divided="vertically" columns="equal">
                <HeaderDetail
                  title={t('language')}
                  content={<Language value={joinRequestFetch.value?.language} />}
                />
                <HeaderDetail title={t('timezone')} content={joinRequestFetch.value?.timeZone} />
              </Grid>
            </HeaderColumn>
          </HeaderGrid>
          <Flex>
            <ModalHeader content={t('textMessage')} />
          </Flex>
          <HeaderGrid columns={1}>
            <HeaderColumn>
              <Grid divided="vertically" columns="equal">
                <HeaderDetail title={joinRequestFetch.value?.freeText} />
              </Grid>
            </HeaderColumn>
          </HeaderGrid>
          {joinRequestFetch.value?.potentialDuplicates?.length > 0 && (
            <>
              <Flex>
                <ModalHeader content={t('potentialDuplicationDetails')} />
              </Flex>
              <Flex direction="column" margin="0 0 1rem 0">
                {joinRequestFetch.value?.potentialDuplicates?.map(duplicate => (
                  <DuplicateRow key={duplicate.id}>
                    {duplicate.isByIdentificationCode ? (
                      <Flex>
                        <Popup
                          inverted
                          hoverable
                          trigger={
                            <NoBreakSpan>
                              <Icon name="id card" />
                            </NoBreakSpan>
                          }
                          content={<span>{t('duplicateByIdentificationCode')}</span>}
                          position="top right"
                          size="small"
                        />
                      </Flex>
                    ) : (
                      <Flex margin="0 0.7rem 0" />
                    )}
                    <DuplicateLabel>{t('organisation')}:</DuplicateLabel>
                    <span>
                      {duplicate.sourceType?.toLowerCase() === SOURCE_TYPES.JOIN_REQUEST ? (
                        <OrgLink
                          to={`/communities/${ACTIONS.EDIT}/${duplicate?.community?.id}/${COMMUNITY_DETAIL_TABS.JOIN_REQUESTS}/${COMMUNITY_TAB_ACTIONS.EDIT_JOIN_REQUEST}/${duplicate.id}`}
                          target="_blank"
                        >
                          {duplicate.organisationName}
                        </OrgLink>
                      ) : (
                        <OrgLink to={`/organisations/${duplicate.id}`} target="_blank">
                          {duplicate.organisationName}
                        </OrgLink>
                      )}
                      {duplicate.city && (
                        <>
                          {' - '}
                          {t('city')}: {duplicate.city}
                        </>
                      )}
                      {duplicate.countryCode && (
                        <>
                          {' - '}
                          {t('country')}: {duplicate.countryCode}
                        </>
                      )}
                      {duplicate?.community?.name && (
                        <>
                          {' - '}
                          {t('community')}: {duplicate?.community?.name}
                        </>
                      )}
                      <HasSomeRights rights={['COMMUNITIES.READ.ALL']}>
                        {duplicate.score !== undefined && (
                          <strong>
                            {' - '}
                            {t('score')}: {duplicate.score}
                          </strong>
                        )}
                      </HasSomeRights>
                    </span>
                  </DuplicateRow>
                ))}
              </Flex>
            </>
          )}
        </Flex>
        {isDuplicateBlocked && (
          <Message info icon="info" content={t('potentialDuplicateCannotManage')} />
        )}
      </Modal.Content>
      <HasSomeRights rights={MANAGE_RIGHTS}>
        <Modal.Actions>
          <ConfirmModal
            header={t('areYouSureYouWantToDenyJoinRequest')}
            cancelContent={t('cancel')}
            confirmContent={t('deny')}
            confirmColor="red"
            onConfirm={handleDenyClick}
            trigger={
              <Button
                content={t('deny')}
                title={t('deny')}
                icon="cancel"
                onClick={setModalActive}
                disabled={
                  joinRequestFetch.pending ||
                  denyJoinRequestFetch.pending ||
                  markUniqueJoinRequestFetch.pending ||
                  isDuplicateBlocked
                }
                color={isDuplicateBlocked ? 'grey' : 'red'}
              />
            }
          />
          {canMarkUnique && (
            <ConfirmModal
              header={t('markNotDuplicateConfirmation')}
              cancelContent={t('cancel')}
              confirmContent={t('confirm')}
              confirmColor="blue"
              onConfirm={handleMarkUniqueClick}
              trigger={
                <Button
                  floated="left"
                  content={t('notADuplicate')}
                  title={t('notADuplicate')}
                  icon="check circle outline"
                  onClick={setModalActive}
                  disabled={
                    joinRequestFetch.pending ||
                    denyJoinRequestFetch.pending ||
                    markUniqueJoinRequestFetch.pending
                  }
                  color="blue"
                />
              }
            />
          )}
          <Button
            content={t('accept')}
            title={t('accept')}
            icon="check"
            onClick={handleAcceptClick}
            disabled={
              joinRequestFetch.pending ||
              denyJoinRequestFetch.pending ||
              markUniqueJoinRequestFetch.pending ||
              isDuplicateBlocked
            }
            color={isDuplicateBlocked ? 'grey' : 'green'}
          />
        </Modal.Actions>
      </HasSomeRights>
    </Modal>
  );
};

JoinRequestDetail.propTypes = {
  dispatchJoinRequestGet: PropTypes.func.isRequired,
  joinRequestFetch: PropTypes.object.isRequired,
  dispatchDenyJoinRequestDelete: PropTypes.func.isRequired,
  denyJoinRequestFetch: PropTypes.object.isRequired,
  dispatchMarkUniqueJoinRequestPut: PropTypes.func.isRequired,
  markUniqueJoinRequestFetch: PropTypes.object.isRequired,
  rootUrl: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
};

export default compose(
  markUniqueJoinRequestService,
  denyJoinRequestService,
  joinRequestService
)(JoinRequestDetail);
